import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { keyframes } from '@emotion/react';
import styled from 'styled-components';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const SIZES = {
  xs: { logo: 20, container: 48 },
  sm: { logo: 38, container: 80 },
  md: { logo: 74, container: 130 },
  lg: { logo: 112, container: 200 },
};

const MensureLogo = ({ size, color }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 432 457"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ display: 'block' }}
  >
    <g fill={color}>
      <g transform="translate(0 161.479)">
        <path
          className="mensure-logo-column mensure-logo-column--one"
          fillRule="evenodd"
          clipRule="evenodd"
          d="M0 57.8842C0 55.5872 1.37697 53.5152 3.49092 52.6322L128.321 0.444222C132.059 -1.11878 136.179 1.63522 136.179 5.69622V289.334C136.179 292.477 133.638 295.025 130.505 295.025H5.6741C2.54061 295.025 0 292.477 0 289.334V57.8842Z"
        />
      </g>
      <g transform="translate(147.527 88.2605)">
        <path
          className="mensure-logo-column mensure-logo-column--two"
          fillRule="evenodd"
          clipRule="evenodd"
          d="M3.05501 65.8135C1.17801 66.7935 0 68.7385 0 70.8615V362.552C0 365.695 2.54102 368.243 5.67401 368.243H130.505C133.639 368.243 136.179 365.695 136.179 362.552V5.69802C136.179 1.42742 131.662 -1.32158 127.885 0.650718L3.05501 65.8135Z"
        />
      </g>
      <g transform="translate(295.054 0)">
        <path
          className="mensure-logo-column mensure-logo-column--three"
          fillRule="evenodd"
          clipRule="evenodd"
          d="M2.59702 81.7328C0.979004 82.7809 0 84.5814 0 86.5138V450.813C0 453.956 2.54102 456.504 5.67401 456.504H130.505C133.639 456.504 136.179 453.956 136.179 450.813V5.69973C136.179 1.19267 131.203 -1.52539 127.427 0.918991L2.59702 81.7328Z"
        />
      </g>
    </g>
  </svg>
);

const StyledLoaderWrapper = styled.div`
  position: relative;
  width: ${props => props.$size}px;
  height: ${props => props.$size}px;
  display: flex;
  align-items: center;
  justify-content: center;

  .mensure-logo-column {
    transform-box: fill-box;
    transform-origin: center bottom;
    animation-duration: 2.35s;
    animation-timing-function: cubic-bezier(0.22, 1, 0.36, 1);
    animation-iteration-count: infinite;
    will-change: transform, opacity;
  }

  .mensure-logo-column--one {
    animation-name: MensureColumnOne;
  }

  .mensure-logo-column--two {
    animation-name: MensureColumnTwo;
  }

  .mensure-logo-column--three {
    animation-name: MensureColumnThree;
  }

  @keyframes MensureColumnOne {
    0%, 6% {
      transform: scaleY(0);
      opacity: 0;
    }
    18%, 86% {
      transform: scaleY(1);
      opacity: 1;
    }
    96%, 100% {
      transform: scaleY(0);
      opacity: 0;
    }
  }

  @keyframes MensureColumnTwo {
    0%, 28% {
      transform: scaleY(0);
      opacity: 0;
    }
    40%, 86% {
      transform: scaleY(1);
      opacity: 1;
    }
    96%, 100% {
      transform: scaleY(0);
      opacity: 0;
    }
  }

  @keyframes MensureColumnThree {
    0%, 50% {
      transform: scaleY(0);
      opacity: 0;
    }
    62%, 86% {
      transform: scaleY(1);
      opacity: 1;
    }
    96%, 100% {
      transform: scaleY(0);
      opacity: 0;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .mensure-logo-column {
      animation: none;
    }
  }
`;

const MensureLoader = ({
  fullScreen = false,
  overlay = false,
  message = '',
  size = 'md',
}) => {
  const theme  = useTheme();
  const s      = SIZES[size] ?? SIZES.md;
  const isDark = theme.palette.mode === 'dark';

  const logoColor = isDark ? '#ffffff' : theme.palette.primary.main;
  const bgColor   = alpha(theme.palette.background.default, 0.96);

  const wrapperSx = fullScreen
    ? {
        position: 'fixed',
        inset: 0,
        zIndex: theme.zIndex.modal + 50,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: bgColor,
        backdropFilter: 'none',
        WebkitBackdropFilter: 'none',
        animation: `${fadeUp} 0.2s ease`,
      }
    : overlay
      ? {
          position: 'absolute',
          inset: 0,
          zIndex: 10,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: bgColor,
          backdropFilter: 'none',
          WebkitBackdropFilter: 'none',
          borderRadius: 'inherit',
        }
      : {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        };

  return (
    <Box sx={wrapperSx}>
      <StyledLoaderWrapper $size={s.container}>
        <Box
          sx={{
            position: 'absolute',
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <MensureLogo size={s.logo} color={logoColor} />
        </Box>
      </StyledLoaderWrapper>

      {message && (
        <Typography
          variant={size === 'xs' || size === 'sm' ? 'caption' : 'body2'}
          color="text.secondary"
          sx={{
            mt: size === 'xs' || size === 'sm' ? 1 : 1.5,
            fontWeight: 500,
            letterSpacing: 0.3,
            animation: `${fadeUp} 0.4s ease 0.1s both`,
          }}
        >
          {message}
        </Typography>
      )}
    </Box>
  );
};

export default MensureLoader;
